import React, { useState } from 'react';
import {FormattedMessage, injectIntl} from "react-intl";
import Modal from "@amzn/meridian/modal";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import { PERFORMANCE_BAR, QUALITY_THRESHOLDS } from "../util/Constants";
import './Inbound.css';

const PerformanceBar = ({percentage, intl}) => {
    percentage = percentage || 0;
    var barWidth = Math.min((percentage / PERFORMANCE_BAR.MAX_SCALE) * 100, 100);
    var benchmarkPos = (PERFORMANCE_BAR.BENCHMARK / PERFORMANCE_BAR.MAX_SCALE) * 100;
    var halfScalePos = (PERFORMANCE_BAR.HALF_SCALE / PERFORMANCE_BAR.MAX_SCALE) * 100;

    var getBarClass = function() {
        if (percentage >= PERFORMANCE_BAR.EXCELLENT_THRESHOLD) return "excellent";
        if (percentage >= PERFORMANCE_BAR.WARNING_THRESHOLD) return "warning";
        return "poor";
    };

    return (
        <div className="progress-wrapper">
            <div
                className="progress-container"
                role="progressbar"
                aria-valuenow={percentage}
                aria-valuemin={0}
                aria-valuemax={PERFORMANCE_BAR.MAX_SCALE}
            >
                <div
                    className="benchmark-line"
                    style={{left: benchmarkPos + '%'}}
                    aria-label={intl.formatMessage({id: 'inbound.benchmark_aria_label', defaultMessage: '100% benchmark'})}
                ></div>
                <div
                    className={`progress-bar ${getBarClass()}`}
                    style={{width: `${barWidth}%`}}
                >
                    <span className="progress-text">{percentage}%</span>
                </div>
            </div>
            <div className="progress-scale">
                <span className="scale-marker">0%</span>
                <span className="scale-marker" style={{position: 'absolute', left: halfScalePos + '%'}}>{PERFORMANCE_BAR.HALF_SCALE}%</span>
                <span className="scale-marker benchmark" style={{position: 'absolute', left: benchmarkPos + '%'}}>{PERFORMANCE_BAR.BENCHMARK}%</span>
                <span className="scale-marker">{PERFORMANCE_BAR.MAX_SCALE}%</span>
            </div>
        </div>
    );
};

const BeltExpandModal = ({open, onClose, line, intl}) => {
    if (!line) return null;
    var lostHours = parseFloat((line.lostHours || 0).toFixed(2));
    var performancePercentage = line.performancePercentage || 0;
    var hasQuality = line.qualityMetrics;

    return (
        <Modal open={open} onClose={onClose} closeLabel="Close" scrollContainer="modal" width={Math.round(window.innerWidth * 0.98)}>
            <Column spacing="medium" spacingInset="medium">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <div style={{fontSize: '72px', fontWeight: '700', color: 'var(--gray-800)', lineHeight: 1.1}}>{line.name}</div>
                </Row>

                <div className="belt-modal-stats-grid">
                    <div className="belt-modal-stat">
                        <span className="belt-modal-stat-label">
                            <FormattedMessage id="inbound.actual_label" defaultMessage="Actual PPH"/>
                        </span>
                        <span className="belt-modal-stat-value">{(line.actual || 0).toLocaleString()}</span>
                    </div>
                    <div className="belt-modal-stat">
                        <span className="belt-modal-stat-label">
                            <FormattedMessage id="inbound.benchmark_label" defaultMessage="Benchmark PPH"/>
                        </span>
                        <span className="belt-modal-stat-value">{(line.benchmark || 0).toLocaleString()}</span>
                    </div>
                    <div className="belt-modal-stat">
                        <span className="belt-modal-stat-label">
                            <FormattedMessage id="inbound.lost_hours_label" defaultMessage="Lost Hours"/>
                        </span>
                        <span className="belt-modal-stat-value">{lostHours}h</span>
                    </div>
                    <div className="belt-modal-stat">
                        <span className="belt-modal-stat-label">
                            <FormattedMessage id="inbound.inducted_packages_label" defaultMessage="Inducted Packages"/>
                        </span>
                        <span className="belt-modal-stat-value">{(line.packageCount || 0).toLocaleString()}</span>
                    </div>
                    <div className="belt-modal-stat">
                        <span className="belt-modal-stat-label">
                            <FormattedMessage id="inbound.inducted_cube_label" defaultMessage="Inducted Cube"/>
                        </span>
                        <span className="belt-modal-stat-value">{(line.inductedCube || 0).toLocaleString()}</span>
                    </div>
                    <div className="belt-modal-stat">
                        <span className="belt-modal-stat-label">
                            <FormattedMessage id="inbound.induct_dt_dpmo_label" defaultMessage="Induct DT DPMO"/>
                        </span>
                        <span className="belt-modal-stat-value">{(line.inductDowntimeDpmo || 0).toLocaleString()}</span>
                    </div>
                    {hasQuality && (
                        <React.Fragment>
                            {line.qualityMetrics.misappliedSalRate !== null && (
                                <div className="belt-modal-stat">
                                    <span className="belt-modal-stat-label">
                                        <FormattedMessage id="inbound.misapplied_sal" defaultMessage="Misapplied SAL"/>
                                    </span>
                                    <span className="belt-modal-stat-value">{line.qualityMetrics.misappliedSalRate.toFixed(1)}%</span>
                                </div>
                            )}
                            {line.qualityMetrics.multiReadRate !== null && (
                                <div className="belt-modal-stat">
                                    <span className="belt-modal-stat-label">
                                        <FormattedMessage id="inbound.multi_read" defaultMessage="Multi-read"/>
                                    </span>
                                    <span className="belt-modal-stat-value">{line.qualityMetrics.multiReadRate.toFixed(1)}%</span>
                                </div>
                            )}
                            {line.qualityMetrics.noReadRate !== null && (
                                <div className="belt-modal-stat">
                                    <span className="belt-modal-stat-label">
                                        <FormattedMessage id="inbound.no_read" defaultMessage="No-read"/>
                                    </span>
                                    <span className="belt-modal-stat-value">{line.qualityMetrics.noReadRate.toFixed(1)}%</span>
                                </div>
                            )}
                        </React.Fragment>
                    )}
                </div>

                <div className="belt-modal-performance-section">
                    <Text type="h200">
                        <FormattedMessage id="inbound.pph_performance" defaultMessage="PPH Performance"/>
                    </Text>
                    <div style={{marginTop: '16px'}}>
                        <PerformanceBar percentage={performancePercentage} intl={intl} />
                    </div>
                </div>

                <Row alignmentHorizontal="right">
                    <Button type="secondary" onClick={onClose}>
                        <FormattedMessage id="inbound.close" defaultMessage="Close"/>
                    </Button>
                </Row>
            </Column>
        </Modal>
    );
};

const LineItem = ({line, intl}) => {
    line = line || {};
    var lostHours = line.lostHours || 0;
    const [modalOpen, setModalOpen] = useState(false);
    var showQuality = line.qualityMetrics;

    return (
        <div className="line-card">
            <div className="line-card-left">
                <span className="line-card-name">{line.name || intl.formatMessage({id: 'inbound.unknown_belt', defaultMessage: 'Unknown'})}</span>
                <div className="line-bar-labels">
                    <div className="meta-item">
                        <span className="meta-label">
                            <FormattedMessage id="inbound.actual_label" defaultMessage="Actual PPH"/>:
                        </span>
                        <span className="meta-value">{(line.actual || 0).toLocaleString()}</span>
                    </div>
                    <div className="meta-item">
                        <span className="meta-label">
                            <FormattedMessage id="inbound.benchmark_label" defaultMessage="Benchmark PPH"/>:
                        </span>
                        <span className="meta-value">{(line.benchmark || 0).toLocaleString()}</span>
                    </div>
                    <div className="meta-item">
                        <span className="meta-label">
                            <FormattedMessage id="inbound.inducted_packages_label" defaultMessage="Inducted Packages"/>:
                        </span>
                        <span className="meta-value">{(line.packageCount || 0).toLocaleString()}</span>
                    </div>
                    <div className="meta-item">
                        <span className="meta-label">
                            <FormattedMessage id="inbound.inducted_cube_label" defaultMessage="Inducted Cube"/>:
                        </span>
                        <span className="meta-value">{(line.inductedCube || 0).toLocaleString()}</span>
                    </div>
                    <div className="meta-item">
                        <span className="meta-label">
                            <FormattedMessage id="inbound.induct_dt_dpmo_label" defaultMessage="Induct DT DPMO"/>:
                        </span>
                        <span className="meta-value">{(line.inductDowntimeDpmo || 0).toLocaleString()}</span>
                    </div>
                </div>
            </div>
            <div className="line-card-quality">
                {showQuality && (
                    <React.Fragment>
                        {line.qualityMetrics.misappliedSalRate !== null && (
                            <div className={`line-quality-badge ${line.qualityMetrics.misappliedSalRate > QUALITY_THRESHOLDS.MISAPPLIED_SAL.critical ? 'quality-badge-critical' : 'quality-badge-normal'}`}>
                                <span>{intl.formatMessage({id: 'inbound.misapplied_sal', defaultMessage: 'Misapplied SAL'})}: {line.qualityMetrics.misappliedSalRate.toFixed(1)}%</span>
                            </div>
                        )}
                        {line.qualityMetrics.multiReadRate !== null && (
                            <div className={`line-quality-badge ${line.qualityMetrics.multiReadRate > QUALITY_THRESHOLDS.MULTI_READ.critical ? 'quality-badge-critical' : 'quality-badge-normal'}`}>
                                <span>{intl.formatMessage({id: 'inbound.multi_read', defaultMessage: 'Multi-read'})}: {line.qualityMetrics.multiReadRate.toFixed(1)}%</span>
                            </div>
                        )}
                        {line.qualityMetrics.noReadRate !== null && (
                            <div className={`line-quality-badge ${line.qualityMetrics.noReadRate > QUALITY_THRESHOLDS.NO_READ.critical ? 'quality-badge-critical' : 'quality-badge-normal'}`}>
                                <span>{intl.formatMessage({id: 'inbound.no_read', defaultMessage: 'No-read'})}: {line.qualityMetrics.noReadRate.toFixed(1)}%</span>
                            </div>
                        )}
                    </React.Fragment>
                )}
                <div className="line-quality-lost-hours">
                    <div className="line-quality-item">
                        <span className="line-quality-label">
                            <FormattedMessage id="inbound.lost_hours_label" defaultMessage="Lost Hours"/>:
                        </span>
                        <span className="line-quality-value">{parseFloat(lostHours.toFixed(2))}h</span>
                    </div>
                </div>
            </div>
            <div className="line-card-right">
                <PerformanceBar percentage={line.performancePercentage || 0} intl={intl} />
            </div>
            <button className="expand-button belt-expand-button" title={intl.formatMessage({id: 'inbound.expand_belt_details', defaultMessage: 'Expand belt details'})} onClick={function() { setModalOpen(true); }}>&#x2922;</button>
            <BeltExpandModal open={modalOpen} onClose={function() { setModalOpen(false); }} line={line} intl={intl} />
        </div>
    );
};

const OperatingLines = ({linesData, intl}) => {
    linesData = linesData || [];
    return (
        <div className="operating-lines-section">
            <h2 className="operating-lines-title">
                <FormattedMessage
                    id="inbound.active_induct_belts"
                    defaultMessage="Active Induct Belts - PPH Performance"
                />
            </h2>
            {linesData.map(function(line, index) {
                return <LineItem key={index} line={line} intl={intl} />;
            })}
        </div>
    );
};

export { OperatingLines, BeltExpandModal };
export default injectIntl(OperatingLines);



// WEBPACK FOOTER //
// ./src/components/inbound/OperatingLines.js